'use client';

import type {
  AvatarFallbackComponentProps,
  AvatarProps,
  AvatarShapes,
  ThemeColorsBackground,
  ThemeColorsMeta,
} from '@frameio/vapor';
import {
  Avatar,
  Box,
  getAvatarColor,
  Skeleton,
  useHoverIntent,
} from '@frameio/vapor';
import type { FileDropZoneProps } from '@frameio/vapor-migration';
import {
  AvatarButton,
  type AvatarButtonProps,
  AvatarHalo,
  AvatarSettings,
} from './components';

const ANIMATION_DURATION_SECONDS = 0.25;
const AVATAR_TRANSITION_DURATION = ANIMATION_DURATION_SECONDS * 1.25;

type AvatarEditorProps = {
  acceptedFileTypes?: string;
  avatarAlt?: string;
  avatarBackground?: string;
  avatarOriginal?: string | null;
  avatarSize?: AvatarProps['size'];
  avatarThumb?: string | null;
  dropzoneLabel?: string;
  editLabel?: string;
  haloClassName?: string;
  onSave: (blob: Blob) => void;
  shape?: AvatarShapes;
  showOverlay?: boolean;
  transitionDuration?: number;
  validationErrorMessage?: string;
  workfrontDefaultAvatar?: React.ComponentType<AvatarFallbackComponentProps>;
} & Pick<FileDropZoneProps, 'validate'> &
  Pick<AvatarProps, 'FallbackAnonymousIconComponent'> &
  Pick<AvatarButtonProps, 'isMenuItem'>;

export function AvatarEditor({
  acceptedFileTypes,
  avatarAlt = '',
  avatarBackground,
  avatarOriginal,
  avatarSize = 88,
  avatarThumb,
  dropzoneLabel,
  editLabel,
  haloClassName,
  FallbackAnonymousIconComponent,
  workfrontDefaultAvatar,
  onSave,
  shape = 'circle',
  showOverlay,
  transitionDuration = AVATAR_TRANSITION_DURATION,
  validate,
  validationErrorMessage,
  isMenuItem,
}: AvatarEditorProps) {
  const { bind: hoverBind } = useHoverIntent(() => {
    if (avatarOriginal === null) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = avatarOriginal;
  });

  return (
    <AvatarSettings
      acceptedFileTypes={acceptedFileTypes}
      avatarSrc={avatarOriginal}
      avatarThumb={avatarThumb}
      dropzoneLabel={dropzoneLabel}
      editLabel={editLabel}
      onSave={onSave}
      shape={shape}
      validate={validate}
      validationErrorMessage={validationErrorMessage}
    >
      {({ nextAvatarSrc, isEditorOpen, ...bind }) => (
        <AvatarButton
          adornment={
            avatarAlt && (
              <Box bottom="0" left="0" position="absolute" top="0">
                <AvatarHalo
                  alt={avatarAlt}
                  className={haloClassName}
                  fallback={
                    (avatarBackground as
                      | ThemeColorsBackground
                      | ThemeColorsMeta) ?? getAvatarColor(avatarAlt)
                  }
                  shape={shape}
                  size={avatarSize}
                  src={nextAvatarSrc || avatarThumb}
                  transitionDelaySeconds={
                    nextAvatarSrc || avatarThumb ? 0.2 : 0
                  }
                  transitionDurationSeconds={transitionDuration}
                />
              </Box>
            )
          }
          /**
           * The conditional check ensures the `Avatar` component will never
           * have the opportunity to render its default anonymous user state.
           * This assumes an `avatarAlt` or `avatarThumb` prop will always be
           * passed to `AvatarEditor`.
           */
          avatar={
            avatarAlt || avatarThumb || FallbackAnonymousIconComponent ? (
              <Avatar
                alt={avatarAlt}
                aria-hidden="true"
                background={
                  avatarBackground as ThemeColorsBackground | ThemeColorsMeta
                }
                FallbackAnonymousIconComponent={
                  workfrontDefaultAvatar || FallbackAnonymousIconComponent
                }
                shape={shape}
                shouldFadeOnLoad={false}
                size={avatarSize}
                src={avatarThumb}
              />
            ) : (
              <Skeleton
                height={`${avatarSize}px`}
                rounded={shape === 'circle'}
                width={`${avatarSize}px`}
              />
            )
          }
          avatarSize={avatarSize}
          delayMount={avatarThumb || avatarAlt ? 0 : 200}
          isMenuItem={isMenuItem}
          label={editLabel}
          nextAvatarSrc={nextAvatarSrc}
          shape={shape}
          showOverlay={isEditorOpen || showOverlay}
          {...bind}
          {...hoverBind}
          data-testid="fio-avatar-editor-btn"
        />
      )}
    </AvatarSettings>
  );
}
